import { AlertTriangle, RefreshCw, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Section that lists rows the filter engine decided NOT to include in the
 * map. Each row offers one-click actions to register a new team member
 * or promote an existing non-team surgeon.
 */
export default function DiscardedRowsSection({
  map,
  stats,
  refiltering,
  addingSurgeon,
  onRefilter,
  onAddAsTeamSurgeon,
  onPromoteToTeam,
}) {
  const discarded = map?.discarded_rows || [];
  const suggested = stats?.suggested_surgeons || [];
  if (discarded.length === 0 && suggested.length === 0) return null;

  return (
    <div
      className="bg-white rounded-md border border-slate-200 shadow-sm p-6 no-print"
      data-testid="discarded-section"
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Linhas descartadas
            <span className="text-sm font-normal text-slate-500">
              ({discarded.length})
            </span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Linhas do mapa bruto que não foram identificadas como cirurgias da sua equipe.
            Se alguma pertence à sua equipe, cadastre o cirurgião com 1 clique abaixo.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onRefilter}
          disabled={refiltering}
          data-testid="btn-refilter"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refiltering ? "animate-spin" : ""}`} />
          Reaplicar filtro
        </Button>
      </div>

      {suggested.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
          <div className="text-xs font-semibold text-amber-900 uppercase tracking-wider mb-2">
            Cirurgiões recorrentes não cadastrados como equipe
          </div>
          <div className="flex flex-wrap gap-2">
            {suggested.map((s) => (
              <div
                key={s.name}
                className="flex items-center gap-1 bg-white border border-amber-300 rounded-md px-2 py-1 text-sm"
                data-testid={`suggestion-${s.name.replace(/\s+/g, "-")}`}
              >
                <span className="font-medium text-slate-800">{s.name}</span>
                <span className="text-xs text-slate-500">({s.count}x)</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-blue-700 hover:bg-blue-50"
                  onClick={() => onAddAsTeamSurgeon(s.name)}
                  disabled={addingSurgeon === s.name}
                  data-testid={`btn-add-suggested-${s.name.replace(/\s+/g, "-")}`}
                >
                  <UserPlus className="h-3 w-3 mr-1" />
                  {addingSurgeon === s.name ? "Adicionando..." : "Cadastrar como equipe"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-slate-200">
          <thead className="bg-slate-50 text-slate-700 font-semibold uppercase tracking-wider">
            <tr>
              <th className="border border-slate-200 px-2 py-1.5 text-left">N°</th>
              <th className="border border-slate-200 px-2 py-1.5 text-left">Período</th>
              <th className="border border-slate-200 px-2 py-1.5 text-left">Paciente</th>
              <th className="border border-slate-200 px-2 py-1.5 text-left">Especialidade</th>
              <th className="border border-slate-200 px-2 py-1.5 text-left">Cirurgia</th>
              <th className="border border-slate-200 px-2 py-1.5 text-left">Cirurgião detectado</th>
              <th className="border border-slate-200 px-2 py-1.5 text-left">Motivo</th>
              <th className="border border-slate-200 px-2 py-1.5 text-left">Ação</th>
            </tr>
          </thead>
          <tbody>
            {discarded.map((d) => (
              <tr
                key={`${d.order}-${d.patient_name || "row"}`}
                className="border-b border-slate-100"
                data-testid={`discarded-row-${d.order}`}
              >
                <td className="border border-slate-200 px-2 py-1.5">{d.order}</td>
                <td className="border border-slate-200 px-2 py-1.5 capitalize">{d.period}</td>
                <td className="border border-slate-200 px-2 py-1.5">{d.patient_name || "—"}</td>
                <td className="border border-slate-200 px-2 py-1.5">{d.specialty || "—"}</td>
                <td className="border border-slate-200 px-2 py-1.5">{d.procedure_name || "—"}</td>
                <td className="border border-slate-200 px-2 py-1.5 font-medium">
                  {d.detected_surgeon || (
                    <span className="text-slate-400 italic">não identificado</span>
                  )}
                </td>
                <td className="border border-slate-200 px-2 py-1.5 text-slate-600">
                  <div>{d.reason}</div>
                  {d.existing_non_team_match && (
                    <div
                      className="mt-1 text-[10px] text-blue-800 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 inline-block"
                      title={`Score: ${d.existing_non_team_match.score}${d.existing_non_team_match.was_fuzzy ? " (aproximação)" : ""}`}
                    >
                      já cadastrado como <strong>{d.existing_non_team_match.full_name}</strong> (não-equipe)
                    </div>
                  )}
                </td>
                <td className="border border-slate-200 px-2 py-1.5">
                  {d.existing_non_team_match ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => onPromoteToTeam(
                        d.existing_non_team_match.id,
                        d.existing_non_team_match.full_name,
                        d.detected_surgeon,
                      )}
                      disabled={addingSurgeon === d.existing_non_team_match.id}
                      data-testid={`btn-promote-discarded-${d.order}`}
                    >
                      <UserPlus className="h-3 w-3 mr-1" /> Promover a equipe
                    </Button>
                  ) : d.detected_surgeon ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-blue-700 hover:bg-blue-50"
                      onClick={() => onAddAsTeamSurgeon(d.detected_surgeon)}
                      disabled={addingSurgeon === d.detected_surgeon}
                      data-testid={`btn-add-discarded-${d.order}`}
                    >
                      <UserPlus className="h-3 w-3 mr-1" /> Cadastrar
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
            {discarded.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center p-4 text-slate-400">
                  Nenhuma linha foi descartada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
